import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import LocationCard from '../../components/LocationCard';
import LocationFormModal from '../../components/LocationFormModal';
import { Location } from '../../types/location';
import { LocationService } from '../../services/location';

const LocationManager: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>();
  const [searchQuery, setSearchQuery] = useState('');

  // Load locations from API on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await LocationService.getAllLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const handleAddLocation = async (locationData: Omit<Location, '_id' | 'createdAt'>) => {
    try {
      const newLocation = await LocationService.addLocation(locationData);
      setLocations([...locations, newLocation]);
      setShowModal(false);
      console.log('Added new location:', newLocation);
    } catch (error: any) {
      console.error('Failed to add location:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add location';
      alert(errorMessage + '. Please make sure you are logged in as an admin and try again.');
    }
  };

  const handleEditLocation = async (locationData: Omit<Location, '_id' | 'createdAt'>) => {
    if (!selectedLocation) return;

    try {
      const updatedLocation = await LocationService.updateLocation(selectedLocation._id, locationData);
      const updatedLocations = locations.map(loc =>
        loc._id === selectedLocation._id ? updatedLocation : loc
      );

      setLocations(updatedLocations);
      setShowModal(false);
      setSelectedLocation(undefined);
      console.log('Updated location:', selectedLocation._id);
    } catch (error) {
      console.error('Failed to update location:', error);
      alert('Failed to update location. Please try again.');
    }
  };

  const handleDeleteLocation = async (_id: string) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await LocationService.deleteLocation(_id);
        setLocations(locations.filter(loc => loc._id !== _id));
        console.log('Deleted location:', _id);
      } catch (error) {
        console.error('Failed to delete location:', error);
        alert('Failed to delete location. Please try again.');
      }
    }
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (location.address && location.address.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Location Manager</h1>
          <button
            onClick={() => {
              setSelectedLocation(undefined);
              setShowModal(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + Add New Location
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full md:w-64 p-2 border rounded"
          />
        </div>

        {filteredLocations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchQuery ? 'No locations found matching your search.' : 'No locations added yet.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLocations.map(location => (
              <LocationCard
                key={location._id}
                location={location}
                onEdit={(loc) => {
                  setSelectedLocation(loc);
                  setShowModal(true);
                }}
                onDelete={() => handleDeleteLocation(location._id)}
              />
            ))}
          </div>
        )}

        {showModal && (
          <LocationFormModal
            location={selectedLocation}
            onSave={selectedLocation ? handleEditLocation : handleAddLocation}
            onClose={() => {
              setShowModal(false);
              setSelectedLocation(undefined);
            }}
            existingLocations={locations}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default LocationManager;
